import { Layout, NotFoundPage } from 'components/core';
import VersionMismatchPage from 'components/core/VersionMismatchPage';
import React from 'react';
import { useIsVersionMismatched } from 'store/ui';
import { Navigate, RouteObject, Outlet } from 'react-router-dom';
import { MemoCompanies } from 'views/companies/table';
import { MemoContract } from 'views/contracts/single';
import { MemoContracts } from 'views/contracts/table';
import { SelectProject } from 'views/projects/selection';
import ReportDownloadView from 'views/reports/ReportDownloadView';
import { DocumentDownloadView } from 'views/reports/documentDownloadView';
import { IssueTable } from '../../views';
import { useSetServiceWorkerConfig } from './helpers';
import { LoginContainer } from './LoginContainer';
import PrivateRoute from './PrivateRoute';
import ProjectRoutes, { getProjectRoutes } from './ProjectRoutes';
import RedirectUnauthorized from './RedirectUnauthorized';
import { MemoAnalyticsRoute } from './routeComponents/analytics';
import {
  AuthorizableCreateCompanyWizard,
  AuthorizableEditCompanyWizard,
} from './routeComponents/company';
import { RedirectCompanyDetailsToEdit } from './RedirectCompanyDetailsToEdit';
import {
  AuthorizableCreateContractWizard,
  AuthorizableEditContractWizard,
} from './routeComponents/contract';
import {
  AuthorizableFieldSettings,
  AuthorizableFieldsSettings,
} from './routeComponents/fieldSettings';
import {
  AuthorizableInspectionWizard,
  AuthorizableInspections,
} from './routeComponents/inspection';
import { AuthorizableTemplateWizard } from './routeComponents/inspectionTemplate';
import { AuthorizableEditOrganizationWizard } from './routeComponents/organization';
import {
  AuthorizableEditProjectWizard,
  AuthorizableProjectSettingsView,
} from './routeComponents/project';
import { AuthorizableSiteView } from './routeComponents/site';
import {
  AuthorizableCreateUserWizard,
  AuthorizableEditUserWizard,
  AuthorizableUser,
  AuthorizableUsers,
} from './routeComponents/user';
import { CurrentUser } from 'views/user/me';
import { APP_PATHS } from './paths';
import {
  organizationIdParam,
  projectIdParam,
  siteIdParam,
  userIdParam,
  inspectionTemplateIdParam,
  chartIdParam,
  companyIdParam,
  contractIdParam,
  customizedFieldNameParam,
} from './routeUrlParams';
import { logError } from 'setup/logger/logError';

// Helper function to convert absolute paths to relative for nested routes
const toRelativePath = (path: string): string => {
  if (typeof path !== 'string') {
    logError('toRelativePath received non-string value:', path);
    throw new Error(`toRelativePath expects a string, got ${typeof path}`);
  }
  return path.startsWith('/') ? path.substring(1) : path;
};

export const getDefaultContainerRoutes = (): RouteObject[] => [
  {
    path: 'select-project',
    // The way out of a project whose data could not be loaded.
    element: <PrivateRoute element={SelectProject} showsContentOnFailure />,
  },
  {
    path: 'issue',
    element: (
      <React.Suspense fallback={null}>
        <PrivateRoute element={IssueTable} />
      </React.Suspense>
    ),
  },
  {
    path: toRelativePath(APP_PATHS.organization.edit(organizationIdParam)),
    element: <PrivateRoute element={AuthorizableEditOrganizationWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.project.edit(projectIdParam)),
    element: <PrivateRoute element={AuthorizableEditProjectWizard} />,
  },
  {
    path: 'project/*',
    element: <ProjectRoutes />,
    children: getProjectRoutes(),
  },
  {
    path: toRelativePath(APP_PATHS.project.settings.root),
    element: <PrivateRoute element={AuthorizableProjectSettingsView} />,
  },
  {
    path: toRelativePath(APP_PATHS.site.single(siteIdParam)),
    element: <PrivateRoute element={AuthorizableSiteView} />,
  },
  {
    path: toRelativePath(APP_PATHS.user.current),
    element: <PrivateRoute element={CurrentUser} />,
  },
  {
    path: toRelativePath(APP_PATHS.dashboard),
    element: <Navigate to={APP_PATHS.issue.root} />,
  },
  {
    path: toRelativePath(APP_PATHS.inspectionTemplate.create),
    element: <PrivateRoute element={AuthorizableTemplateWizard} />,
  },
  {
    path: toRelativePath(
      APP_PATHS.inspectionTemplate.edit(inspectionTemplateIdParam)
    ),
    element: <PrivateRoute element={AuthorizableTemplateWizard} />,
  },
  {
    path: toRelativePath(
      APP_PATHS.inspection.create(inspectionTemplateIdParam)
    ),
    element: <PrivateRoute element={AuthorizableInspectionWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.inspection.root),
    element: <PrivateRoute element={AuthorizableInspections} />,
  },
  {
    path: toRelativePath(APP_PATHS.analytics.single(chartIdParam)),
    element: <PrivateRoute element={MemoAnalyticsRoute} />,
  },
  {
    path: toRelativePath(APP_PATHS.analytics.root),
    element: <PrivateRoute element={MemoAnalyticsRoute} />,
  },
  {
    path: toRelativePath(APP_PATHS.company.root),
    element: <PrivateRoute element={MemoCompanies} />,
  },
  {
    path: toRelativePath(APP_PATHS.company.edit(companyIdParam)),
    element: <PrivateRoute element={AuthorizableEditCompanyWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.company.details(companyIdParam)),
    element: <PrivateRoute element={RedirectCompanyDetailsToEdit} />,
  },
  {
    path: toRelativePath(APP_PATHS.contract.root),
    element: <PrivateRoute element={MemoContracts} />,
  },
  {
    path: toRelativePath(APP_PATHS.contract.edit(contractIdParam)),
    element: <PrivateRoute element={AuthorizableEditContractWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.contract.details(contractIdParam)),
    element: <PrivateRoute element={MemoContract} />,
  },
  {
    path: toRelativePath(APP_PATHS.company.create),
    element: <PrivateRoute element={AuthorizableCreateCompanyWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.contract.create),
    element: <PrivateRoute element={AuthorizableCreateContractWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.user.root),
    element: <PrivateRoute element={AuthorizableUsers} />,
  },
  {
    path: toRelativePath(APP_PATHS.user.create),
    element: <PrivateRoute element={AuthorizableCreateUserWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.user.single(userIdParam)),
    element: <PrivateRoute element={AuthorizableUser} />,
  },
  {
    path: toRelativePath(APP_PATHS.user.edit(userIdParam)),
    element: <PrivateRoute element={AuthorizableEditUserWizard} />,
  },
  {
    path: toRelativePath(APP_PATHS.fieldsSettings.root),
    element: <PrivateRoute element={AuthorizableFieldsSettings} />,
  },
  {
    path: toRelativePath(
      APP_PATHS.fieldsSettings.customField(customizedFieldNameParam)
    ),
    element: <PrivateRoute element={AuthorizableFieldSettings} />,
  },
  {
    index: true,
    element: <Navigate to='/issue' />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

const VersionCheckWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const mismatchedVersion = useIsVersionMismatched();

  useSetServiceWorkerConfig();

  return mismatchedVersion ? <VersionMismatchPage /> : <>{children}</>;
};

export const getRouteConfig = (): RouteObject[] => [
  {
    path: '/report/:reportId/download',
    element: (
      <VersionCheckWrapper>
        <ReportDownloadView />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/shared/issue/:issueId/event/:eventId/document/:documentId/download',
    element: (
      <VersionCheckWrapper>
        <DocumentDownloadView />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/shared/issue/:issueId/document/:documentId/download',
    element: (
      <VersionCheckWrapper>
        <DocumentDownloadView />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/shared/issue/:issueId/incomingEmailMessage/:incomingEmailMessageId/document/:documentId/download',
    element: (
      <VersionCheckWrapper>
        <DocumentDownloadView />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/shared/issue/:issueId/documentation/:linkedDocumentationId/download',
    element: (
      <VersionCheckWrapper>
        <DocumentDownloadView />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/redirect',
    element: (
      <VersionCheckWrapper>
        <RedirectUnauthorized />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/login',
    element: (
      <VersionCheckWrapper>
        <LoginContainer />
      </VersionCheckWrapper>
    ),
  },
  {
    path: '/*',
    element: (
      <VersionCheckWrapper>
        <Layout>
          <Outlet />
        </Layout>
      </VersionCheckWrapper>
    ),
    children: getDefaultContainerRoutes(),
  },
];

const HustroRoutes = (): React.ReactElement => {
  // This component is kept for backward compatibility but not used anymore
  return <div>Routes moved to object configuration</div>;
};

export default HustroRoutes;
